import React, { useEffect, useMemo, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../utils/CONSTANTS";
import Header from "../../components/header";
import { canManageWireStockDelete } from "../../utils/roles";

const handleArrowKeyNavigation = (e) => {
  const row = parseInt(e.target.dataset.row, 10);
  const col = parseInt(e.target.dataset.col, 10);
  const grid = e.target.dataset.grid || "";

  if (isNaN(row) || isNaN(col)) return;

  let nextRow = row;
  let nextCol = col;

  switch (e.key) {
    case "ArrowUp":
      nextRow = row - 1;
      break;
    case "ArrowDown":
      nextRow = row + 1;
      break;
    case "ArrowLeft":
      nextCol = col - 1;
      break;
    case "ArrowRight":
      nextCol = col + 1;
      break;
    default:
      return;
  }

  const gridAttr = grid ? `[data-grid="${grid}"]` : "";
  const nextInput = document.querySelector(
    `input${gridAttr}[data-row="${nextRow}"][data-col="${nextCol}"], select${gridAttr}[data-row="${nextRow}"][data-col="${nextCol}"]`
  );

  if (nextInput) {
    nextInput.focus();
    if (typeof nextInput.select === "function") {
      nextInput.select();
    }
    e.preventDefault();
  }
};

const emptyBundles = () =>
  Array.from({ length: 11 }, (_, i) => ({
    bundleNo: i + 1,
    diaAsPerVendor: "",
    recordedDia: "",
    tensileAsPerVendor: "",
    recordedTensile: "",
    acceptedRejected: "",
    remark: "",
    tagsShelfNumber: "",
  }));

const initialForm = (documentNo = "") => ({
  documentNo,
  documentSeq: null,
  reviseNo: "",
  invoiceDate: new Date().toISOString().split("T")[0],
  mtcNo: "",
  mtcDate: "",
  vendorMaterialName: "",
  vendorGrade: "",
  vendorQuantity: "",
  vendorDia: "",
  vendorTensile: "",
  wireName: "",
  inspectionMaterialName: "",
  recordedGrade: "",
  quantityReceived: "",
  recordedDia: "",
  recordedTensile: "",
  bundles: emptyBundles(),
  checkedBy: "",
  verifiedBy: "",
});

const cellInput =
  "w-full border border-gray-400 px-1 py-0.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-teal-600";

const WirePurchase = () => {
  const canClearWireStock = canManageWireStockDelete();
  const [formData, setFormData] = useState(() => initialForm(""));
  const [wireNames, setWireNames] = useState([]);
  const [wires, setWires] = useState([]);
  const [stockSearch, setStockSearch] = useState("");
  const [sortType, setSortType] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [isSaving, setIsSaving] = useState(false);
  const [recentInspections, setRecentInspections] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [nextDocumentNo, setNextDocumentNo] = useState("");
  const [statusModal, setStatusModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const toDateInput = (value) => {
    if (!value) return "";
    return String(value).split("T")[0];
  };

  const showStatusModal = (title, message) => {
    setStatusModal({ open: true, title, message });
  };

  const closeStatusModal = () => {
    setStatusModal({ open: false, title: "", message: "" });
  };

  useEffect(() => {
    fetchWireNames();
    getAllWires();
    fetchRecentInspections();
    fetchNextDocumentNo();
  }, []);

  const fetchNextDocumentNo = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/wire/incoming-inspection/next-doc-no`,
        { withCredentials: true }
      );
      const documentNo = response.data.documentNo;
      const maxvalue = response.data.maxvalue;
      setNextDocumentNo(documentNo);
      setFormData((prev) => ({
        ...prev,
        documentNo,
        documentSeq: maxvalue,
      }));
      return { documentNo, maxvalue };
    } catch (err) {
      console.error("Error fetching next document no:", err);
      toast.error("Failed to fetch next document number");
      return null;
    }
  };

  const fetchWireNames = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wire/names`, {
        withCredentials: true,
      });
      const names = (response.data.names || [])
        .map((item) => item.name)
        .sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base", numeric: true })
        );
      setWireNames(names);
    } catch (error) {
      console.error("Error fetching wire names:", error);
      toast.error("Failed to fetch wire names");
    }
  };

  const getAllWires = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wire/getwireall`, {
        withCredentials: true,
      });
      const list = (response.data.names || []).map((item, index) => ({
        index,
        name: item.name,
        purchaseDate: item.purchaseDate,
        wirequantity: item.wirequantity,
      }));
      setWires(list);
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to load wire stock");
    }
  };

  const fetchRecentInspections = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/wire/incoming-inspection`,
        { withCredentials: true }
      );
      setRecentInspections(response.data.inspections || []);
    } catch (err) {
      console.error("Error fetching inspections:", err);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const vendorToInspectionMap = {
    vendorMaterialName: ["wireName", "inspectionMaterialName"],
    vendorGrade: ["recordedGrade"],
    vendorQuantity: ["quantityReceived"],
    vendorDia: ["recordedDia"],
    vendorTensile: ["recordedTensile"],
  };

  const handleVendorChange = (name, value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      const targets = vendorToInspectionMap[name] || [];
      targets.forEach((key) => {
        next[key] = value;
      });
      return next;
    });
  };

  const handleVendorMaterialSelect = (wireName) => {
    handleVendorChange("vendorMaterialName", wireName);
  };

  const handleWireSelect = (wireName) => {
    setFormData((prev) => ({
      ...prev,
      wireName,
      inspectionMaterialName: wireName,
    }));
  };

  const handleBundleChange = (index, field, value) => {
    setFormData((prev) => {
      const bundles = [...prev.bundles];
      bundles[index] = { ...bundles[index], [field]: value };
      return { ...prev, bundles };
    });
  };

  const resetForm = async () => {
    setIsEditMode(false);
    const next = await fetchNextDocumentNo();
    setFormData({
      ...initialForm(next?.documentNo || ""),
      documentSeq: next?.maxvalue ?? null,
    });
  };

  const mapInspectionToForm = (inspection) => {
    const bundlesFromDb = Array.isArray(inspection.bundles)
      ? inspection.bundles
      : [];
    const bundles = emptyBundles().map((row, index) => {
      const existing = bundlesFromDb.find(
        (b) => Number(b.bundleNo) === index + 1
      ) || bundlesFromDb[index];
      return existing
        ? {
            bundleNo: index + 1,
            diaAsPerVendor: existing.diaAsPerVendor || "",
            recordedDia: existing.recordedDia || "",
            tensileAsPerVendor: existing.tensileAsPerVendor || "",
            recordedTensile: existing.recordedTensile || "",
            acceptedRejected: existing.acceptedRejected || "",
            remark: existing.remark || "",
            tagsShelfNumber: existing.tagsShelfNumber || "",
          }
        : row;
    });

    return {
      documentNo: inspection.documentNo,
      documentSeq: inspection.documentSeq ?? inspection.documentNo,
      reviseNo: inspection.reviseNo || "",
      invoiceDate: toDateInput(inspection.invoiceDate) || new Date().toISOString().split("T")[0],
      mtcNo: inspection.mtcNo || "",
      mtcDate: toDateInput(inspection.mtcDate),
      vendorMaterialName: inspection.vendorMaterialName || inspection.wireName || "",
      vendorGrade: inspection.vendorGrade || "",
      vendorQuantity: inspection.vendorQuantity || "",
      vendorDia: inspection.vendorDia || "",
      vendorTensile: inspection.vendorTensile || "",
      wireName: inspection.wireName || "",
      inspectionMaterialName:
        inspection.inspectionMaterialName || inspection.wireName || "",
      recordedGrade: inspection.recordedGrade || "",
      quantityReceived:
        inspection.quantityReceived != null
          ? String(inspection.quantityReceived)
          : "",
      recordedDia: inspection.recordedDia || "",
      recordedTensile: inspection.recordedTensile || "",
      bundles,
      checkedBy: inspection.checkedBy || "",
      verifiedBy: inspection.verifiedBy || "",
    };
  };

  const loadInspectionByDocNo = async (documentNo) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/wire/incoming-inspection/by-doc/${documentNo}`,
        { withCredentials: true }
      );
      const inspection = response.data.inspection;
      setFormData(mapInspectionToForm(inspection));
      setIsEditMode(true);
    } catch (error) {
      console.error("Error loading inspection:", error);
      toast.error(
        error?.response?.data?.message || "Failed to load document"
      );
    }
  };

  const handleDocumentSelect = async (value) => {
    if (value === "__new__" || value === "") {
      await resetForm();
      return;
    }
    await loadInspectionByDocNo(value);
  };

  const buildPayload = () => {
    const qty = Number(formData.quantityReceived);
    return {
      ...formData,
      quantityReceived: qty,
      inspectionMaterialName:
        formData.inspectionMaterialName || formData.wireName,
    };
  };

  const validateForm = () => {
    if (!formData.wireName) {
      toast.error("Select Material / Wire Name");
      return false;
    }
    const qty = Number(formData.quantityReceived);
    if (isNaN(qty) || qty <= 0) {
      toast.error("Quantity Received must be greater than 0");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/wire/incoming-inspection`,
        buildPayload(),
        { withCredentials: true }
      );

      if (response.status === 201 || response.status === 200) {
        const docNo =
          response.data?.inspection?.documentNo || formData.documentNo;
        await getAllWires();
        await fetchRecentInspections();
        await resetForm();
        showStatusModal(
          "Saved",
          `Document No. ${docNo} saved successfully. Wire stock updated.`
        );
      }
    } catch (error) {
      console.error("Error saving inspection:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save incoming inspection"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!isEditMode) {
      toast.error("Select an existing Document No. to update");
      return;
    }
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/wire/incoming-inspection/${formData.documentNo}`,
        buildPayload(),
        { withCredentials: true }
      );

      if (response.status === 200) {
        await getAllWires();
        await fetchRecentInspections();
        showStatusModal(
          "Updated",
          `Document No. ${formData.documentNo} updated successfully.`
        );
      }
    } catch (error) {
      console.error("Error updating inspection:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update inspection"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !formData.documentNo) {
      toast.error("Select an existing Document No. to delete");
      return;
    }

    setIsSaving(true);
    try {
      const docNo = formData.documentNo;
      const response = await axios.delete(
        `${API_BASE_URL}/api/wire/incoming-inspection/${docNo}`,
        { withCredentials: true }
      );

      if (response.status === 200) {
        setShowDeleteConfirm(false);
        await getAllWires();
        await fetchRecentInspections();
        await resetForm();
        showStatusModal(
          "Deleted",
          `Document No. ${docNo} deleted successfully. Wire stock adjusted.`
        );
      }
    } catch (error) {
      console.error("Error deleting inspection:", error);
      toast.error(
        error?.response?.data?.message || "Failed to delete inspection"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearStock = async (name, qty) => {
    if (
      !window.confirm(
        `Clear stock for "${name}" (${qty} Kg)? This sets quantity to 0.`
      )
    ) {
      return;
    }
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/wire/register`,
        {
          wireName: name,
          wireQuantity: -1 * Number(qty),
          purchaseDate: new Date().toISOString().split("T")[0],
        },
        { withCredentials: true }
      );
      if (response.status === 200) {
        toast.success("Stock cleared");
        await getAllWires();
      }
    } catch (error) {
      console.error("Error clearing stock:", error);
      toast.error(
        error?.response?.data?.message || "Failed to clear wire stock"
      );
    }
  };

  const filteredStock = useMemo(() => {
    const q = stockSearch.trim().toLowerCase();
    let list = [...wires];
    if (q) {
      list = list.filter((w) => String(w.name).toLowerCase().includes(q));
    }
    const dir = sortDirection === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sortType) {
        case "quantity":
          return (Number(a.wirequantity) - Number(b.wirequantity)) * dir;
        case "purchaseDate":
          return (
            (new Date(a.purchaseDate || 0) - new Date(b.purchaseDate || 0)) *
            dir
          );
        case "name":
        default:
          return (
            String(a.name).localeCompare(String(b.name), undefined, {
              sensitivity: "base",
              numeric: true,
            }) * dir
          );
      }
    });
    return list;
  }, [wires, stockSearch, sortType, sortDirection]);

  return (
    <>
      <Header />
      <div className="p-3 bg-gray-100 min-h-screen">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 xl:grid-cols-[minmax(0,1.7fr)_minmax(280px,0.9fr)] gap-4">
          {/* INSPECTION FORM */}
          <div className="bg-white border border-black shadow-md p-3">
            <div className="text-center border-b border-black pb-2 mb-2">
              <h2 className="text-lg font-bold tracking-wide uppercase">
                Precision Products
              </h2>
              <h1 className="text-base font-semibold mt-1 underline underline-offset-2">
                INCOMING MATERIAL INSPECTION
              </h1>
            </div>

            {/* Meta */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <tbody>
                <tr>
                  <th className="border border-black bg-gray-200 p-1 text-left w-[14%]">
                    Document No.
                  </th>
                  <td className="border border-black p-1 w-[18%]">
                    <div className="relative">
                      <select
                        className={`${cellInput} appearance-none pr-5`}
                        value={
                          isEditMode
                            ? String(formData.documentNo ?? "")
                            : "__new__"
                        }
                        onChange={(e) => handleDocumentSelect(e.target.value)}
                      >
                        <option value="__new__">
                          New ({nextDocumentNo || "..."})
                        </option>
                        {[...recentInspections]
                          .sort(
                            (a, b) =>
                              Number(b.documentNo) - Number(a.documentNo)
                          )
                          .map((item) => (
                            <option
                              key={item._id || item.documentNo}
                              value={String(item.documentNo)}
                            >
                              {item.documentNo}
                              {item.wireName ? ` — ${item.wireName}` : ""}
                            </option>
                          ))}
                      </select>
                      <FaCaretDown className="absolute right-1.5 top-1.5 text-gray-400 w-2.5 h-2.5 pointer-events-none" />
                    </div>
                  </td>
                  <th className="border border-black bg-gray-200 p-1 text-left w-[12%]">
                    Revise No.
                  </th>
                  <td className="border border-black p-1 w-[12%]">
                    <input
                      className={cellInput}
                      data-grid="meta"
                      data-row="0"
                      data-col="1"
                      value={formData.reviseNo}
                      onChange={(e) => handleChange("reviseNo", e.target.value)}
                      onKeyDown={handleArrowKeyNavigation}
                      placeholder="Revise No."
                    />
                  </td>
                  <th className="border border-black bg-gray-200 p-1 text-left w-[14%]">
                    Invoice Date
                  </th>
                  <td className="border border-black p-1">
                    <input
                      type="date"
                      className={cellInput}
                      data-grid="meta"
                      data-row="0"
                      data-col="2"
                      value={formData.invoiceDate}
                      onChange={(e) =>
                        handleChange("invoiceDate", e.target.value)
                      }
                      onKeyDown={handleArrowKeyNavigation}
                    />
                  </td>
                </tr>
                <tr>
                  <th className="border border-black bg-gray-200 p-1 text-left">
                    MTC No.
                  </th>
                  <td className="border border-black p-1" colSpan={2}>
                    <input
                      className={cellInput}
                      data-grid="meta"
                      data-row="1"
                      data-col="0"
                      value={formData.mtcNo}
                      onChange={(e) => handleChange("mtcNo", e.target.value)}
                      onKeyDown={handleArrowKeyNavigation}
                      placeholder="MTC No."
                    />
                  </td>
                  <th className="border border-black bg-gray-200 p-1 text-left">
                    MTC Date
                  </th>
                  <td className="border border-black p-1" colSpan={2}>
                    <input
                      type="date"
                      className={cellInput}
                      data-grid="meta"
                      data-row="1"
                      data-col="1"
                      value={formData.mtcDate}
                      onChange={(e) => handleChange("mtcDate", e.target.value)}
                      onKeyDown={handleArrowKeyNavigation}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Vendor vs Inspection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mb-2">
              <table className="w-full border-collapse border border-black text-xs">
                <thead>
                  <tr className="bg-gray-200">
                    <th
                      colSpan={2}
                      className="border border-black p-1.5 text-center font-semibold"
                    >
                      Details as per Vendor / Invoice
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left w-[40%]">
                      Material Name
                    </th>
                    <td className="border border-black p-1">
                      <div className="relative">
                        <select
                          className={`${cellInput} appearance-none pr-5`}
                          data-grid="details"
                          data-row="0"
                          data-col="0"
                          value={formData.vendorMaterialName}
                          onChange={(e) =>
                            handleVendorMaterialSelect(e.target.value)
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        >
                          <option value="">Select wire</option>
                          {wireNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <FaCaretDown className="absolute right-1.5 top-1.5 text-gray-400 w-2.5 h-2.5 pointer-events-none" />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Grade
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="1"
                        data-col="0"
                        value={formData.vendorGrade}
                        onChange={(e) =>
                          handleVendorChange("vendorGrade", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Quantity
                    </th>
                    <td className="border border-black p-1">
                      <input
                        type="number"
                        step="any"
                        className={cellInput}
                        data-grid="details"
                        data-row="2"
                        data-col="0"
                        value={formData.vendorQuantity}
                        onChange={(e) =>
                          handleVendorChange("vendorQuantity", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Dia (mm)
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="3"
                        data-col="0"
                        value={formData.vendorDia}
                        onChange={(e) =>
                          handleVendorChange("vendorDia", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Tensile Strength
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="4"
                        data-col="0"
                        value={formData.vendorTensile}
                        onChange={(e) =>
                          handleVendorChange("vendorTensile", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className="w-full border-collapse border border-black text-xs">
                <thead>
                  <tr className="bg-gray-200">
                    <th
                      colSpan={2}
                      className="border border-black p-1.5 text-center font-semibold"
                    >
                      Details as per Inspection
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left w-[40%]">
                      Material Name (Wire)
                    </th>
                    <td className="border border-black p-1">
                      <div className="relative">
                        <select
                          className={`${cellInput} appearance-none pr-5`}
                          data-grid="details"
                          data-row="0"
                          data-col="1"
                          value={formData.wireName}
                          onChange={(e) => handleWireSelect(e.target.value)}
                          onKeyDown={handleArrowKeyNavigation}
                        >
                          <option value="">Select wire</option>
                          {wireNames.map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                        <FaCaretDown className="absolute right-1.5 top-1.5 text-gray-400 w-2.5 h-2.5 pointer-events-none" />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Recorded Grade
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="1"
                        data-col="1"
                        value={formData.recordedGrade}
                        onChange={(e) =>
                          handleChange("recordedGrade", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-teal-100 p-1 text-left">
                      Quantity Received (Kg) *
                    </th>
                    <td className="border border-black p-1">
                      <input
                        type="number"
                        step="any"
                        min="0"
                        className={`${cellInput} font-semibold bg-teal-50`}
                        data-grid="details"
                        data-row="2"
                        data-col="1"
                        value={formData.quantityReceived}
                        onChange={(e) =>
                          handleChange("quantityReceived", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                        placeholder="Increases wire stock"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Recorded Dia (mm)
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="3"
                        data-col="1"
                        value={formData.recordedDia}
                        onChange={(e) =>
                          handleChange("recordedDia", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th className="border border-black bg-gray-100 p-1 text-left">
                      Recorded Tensile Strength
                    </th>
                    <td className="border border-black p-1">
                      <input
                        className={cellInput}
                        data-grid="details"
                        data-row="4"
                        data-col="1"
                        value={formData.recordedTensile}
                        onChange={(e) =>
                          handleChange("recordedTensile", e.target.value)
                        }
                        onKeyDown={handleArrowKeyNavigation}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Bundles */}
            <div className="overflow-x-auto mb-3">
              <table className="w-full border-collapse border border-black text-[11px] min-w-[900px]">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border border-black p-1">Bundle</th>
                    <th className="border border-black p-1">
                      Dia as per Vendor (mm)
                    </th>
                    <th className="border border-black p-1">
                      Recorded Dia (mm)
                    </th>
                    <th className="border border-black p-1">
                      Tensile Strength as per Vendor
                    </th>
                    <th className="border border-black p-1">
                      Recorded Tensile Strength (MPa)
                    </th>
                    <th className="border border-black p-1">
                      Accepted / Rejected
                    </th>
                    <th className="border border-black p-1">Remark</th>
                    <th className="border border-black p-1">
                      Coded by Tags &amp; Shelf Number
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {formData.bundles.map((row, index) => (
                    <tr key={row.bundleNo}>
                      <td className="border border-black p-1 text-center font-medium">
                        {row.bundleNo}
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="0"
                          value={row.diaAsPerVendor}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "diaAsPerVendor",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="1"
                          value={row.recordedDia}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "recordedDia",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="2"
                          value={row.tensileAsPerVendor}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "tensileAsPerVendor",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="3"
                          value={row.recordedTensile}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "recordedTensile",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                      <td className="border border-black p-0.5 min-w-[120px]">
                        <select
                          className="w-full border border-gray-400 px-1 py-0.5 text-xs bg-white cursor-pointer focus:outline-none focus:ring-1 focus:ring-teal-600"
                          data-grid="bundles"
                          data-row={index}
                          data-col="4"
                          value={row.acceptedRejected}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "acceptedRejected",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        >
                          <option value="">-- Select --</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Rejected">Rejected</option>
                        </select>
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="5"
                          value={row.remark}
                          onChange={(e) =>
                            handleBundleChange(index, "remark", e.target.value)
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                      <td className="border border-black p-0.5">
                        <input
                          className={cellInput}
                          data-grid="bundles"
                          data-row={index}
                          data-col="6"
                          value={row.tagsShelfNumber}
                          onChange={(e) =>
                            handleBundleChange(
                              index,
                              "tagsShelfNumber",
                              e.target.value
                            )
                          }
                          onKeyDown={handleArrowKeyNavigation}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer signatures */}
            <table className="w-full border-collapse border border-black text-xs mb-3">
              <tbody>
                <tr>
                  <th className="border border-black bg-gray-200 p-1 text-left w-[20%]">
                    Checked By
                  </th>
                  <td className="border border-black p-1 w-[30%]">
                    <input
                      className={cellInput}
                      data-grid="footer"
                      data-row="0"
                      data-col="0"
                      value={formData.checkedBy}
                      onChange={(e) =>
                        handleChange("checkedBy", e.target.value)
                      }
                      onKeyDown={handleArrowKeyNavigation}
                    />
                  </td>
                  <th className="border border-black bg-gray-200 p-1 text-left w-[20%]">
                    Verified By
                  </th>
                  <td className="border border-black p-1 w-[30%]">
                    <input
                      className={cellInput}
                      data-grid="footer"
                      data-row="0"
                      data-col="1"
                      value={formData.verifiedBy}
                      onChange={(e) =>
                        handleChange("verifiedBy", e.target.value)
                      }
                      onKeyDown={handleArrowKeyNavigation}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="flex justify-between items-center bg-teal-700 p-2 gap-2 flex-wrap">
              <button
                type="button"
                onClick={resetForm}
                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
              >
                Clear / New
              </button>
              <div className="flex gap-2">
                {isEditMode ? (
                  <>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={() => setShowDeleteConfirm(true)}
                      className={`px-4 py-1 rounded text-xs text-white ${
                        isSaving
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-600 hover:bg-red-700"
                      }`}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      disabled={isSaving}
                      onClick={handleUpdate}
                      className={`px-4 py-1 rounded text-xs text-white ${
                        isSaving
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-700 hover:bg-blue-800"
                      }`}
                    >
                      {isSaving ? "Updating..." : "Update"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={handleSave}
                    className={`px-4 py-1 rounded text-xs text-white ${
                      isSaving
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-black hover:bg-gray-800"
                    }`}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* STOCK PANEL */}
          <div className="bg-white border border-gray-300 shadow-md rounded p-3 h-fit xl:sticky xl:top-3">
            <h2 className="text-sm font-semibold mb-2 border-b pb-1">
              Wire Purchase Stock
            </h2>

            <input
              type="text"
              placeholder="Search wire..."
              value={stockSearch}
              onChange={(e) => setStockSearch(e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs mb-2"
            />

            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="block text-[10px] text-gray-600 mb-0.5">
                  Sort By
                </label>
                <select
                  value={sortType}
                  onChange={(e) => setSortType(e.target.value)}
                  className="w-full border rounded p-1 text-xs"
                >
                  <option value="name">Wire Name</option>
                  <option value="quantity">Quantity</option>
                  <option value="purchaseDate">Purchase Date</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-600 mb-0.5">
                  Direction
                </label>
                <select
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  className="w-full border rounded p-1 text-xs"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto border border-gray-200">
              <table className="w-full border-collapse text-xs">
                <thead className="sticky top-0 bg-gray-200">
                  <tr>
                    <th className="border border-gray-300 px-2 py-1 text-left">
                      #
                    </th>
                    <th className="border border-gray-300 px-2 py-1 text-left">
                      Wire Name
                    </th>
                    <th className="border border-gray-300 px-2 py-1 text-left">
                      Qty (Kg)
                    </th>
                    <th className="border border-gray-300 px-2 py-1 text-left">
                      Last Purchase
                    </th>
                    {canClearWireStock && (
                    <th className="border border-gray-300 px-2 py-1 text-left">
                      Clear
                    </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredStock.length === 0 ? (
                    <tr>
                      <td
                        colSpan={canClearWireStock ? 5 : 4}
                        className="border border-gray-300 p-3 text-center text-gray-500"
                      >
                        No wires found
                      </td>
                    </tr>
                  ) : (
                    filteredStock.map((item, idx) => (
                      <tr key={item.name} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-2 py-1">
                          {idx + 1}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 font-medium">
                          {item.name}
                        </td>
                        <td className="border border-gray-300 px-2 py-1">
                          {Number(item.wirequantity || 0).toFixed(3)}
                        </td>
                        <td className="border border-gray-300 px-2 py-1">
                          {item.purchaseDate
                            ? String(item.purchaseDate).split("T")[0]
                            : "-"}
                        </td>
                        {canClearWireStock && (
                        <td className="border border-gray-300 px-2 py-1">
                          <button
                            type="button"
                            title="Clear stock"
                            onClick={() =>
                              handleClearStock(item.name, item.wirequantity)
                            }
                            className="hover:opacity-70"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                            >
                              <path d="M 10.806641 2 C 10.289641 2 9.7956875 2.2043125 9.4296875 2.5703125 L 9 3 L 4 3 A 1.0001 1.0001 0 1 0 4 5 L 20 5 A 1.0001 1.0001 0 1 0 20 3 L 15 3 L 14.570312 2.5703125 C 14.205312 2.2043125 13.710359 2 13.193359 2 L 10.806641 2 z M 4.3652344 7 L 5.8925781 20.263672 C 6.0245781 21.253672 6.877 22 7.875 22 L 16.123047 22 C 17.121047 22 17.974422 21.254859 18.107422 20.255859 L 19.634766 7 L 4.3652344 7 z"></path>
                            </svg>
                          </button>
                        </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-5 rounded-md shadow-md max-w-sm w-full mx-3">
            <h2 className="text-lg font-semibold mb-2">Confirm Delete</h2>
            <p className="text-sm text-gray-700 mb-4">
              Delete Document No. <strong>{formData.documentNo}</strong>? Wire
              stock will be reduced by the Quantity Received.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1 bg-gray-200 rounded text-sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                disabled={isSaving}
                onClick={handleDelete}
              >
                {isSaving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-5 rounded-md shadow-md max-w-sm w-full mx-3 text-center">
            <h2 className="text-lg font-semibold mb-2 text-teal-800">
              {statusModal.title}
            </h2>
            <p className="text-sm text-gray-700 mb-4">{statusModal.message}</p>
            <button
              type="button"
              className="px-4 py-1.5 bg-teal-700 text-white rounded text-sm hover:bg-teal-800"
              onClick={closeStatusModal}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default WirePurchase;
